import { useCallback, useEffect, useRef, useState } from "react";
import { fitVideo, needsFit, VideoFitError } from "./videoFit";

/** A file's size the way the pickers show it: KB under a megabyte, else MB. */
export function sizeText(bytes: number): string {
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * useVideoFit is a picked file made sendable (videoFit.ts) for a picker: fit
 * hands back the file to keep — the one picked when it needs nothing, else
 * the clip made small enough — and line says how far it has got meanwhile.
 * stop drops one under way; fit then rejects with null as its reason, which
 * whatFailed turns into nothing to say. Leaving the page stops it too.
 */
export function useVideoFit() {
  const [line, setLine] = useState("");
  const ctl = useRef<AbortController | null>(null);
  useEffect(() => () => ctl.current?.abort(), []);

  const fit = useCallback(async (f: File): Promise<File> => {
    if (!needsFit(f)) return f;
    ctl.current?.abort();
    const c = new AbortController();
    ctl.current = c;
    const was = sizeText(f.size);
    setLine(`正在压缩影片…（原本 ${was}）`);
    try {
      const out = await fitVideo(f, (d) => {
        if (!c.signal.aborted) setLine(`正在压缩影片 ${Math.round(d * 100)}%（原本 ${was}）`);
      }, c.signal);
      if (c.signal.aborted) throw null;
      return out;
    } catch (e) {
      throw c.signal.aborted ? null : e;
    } finally {
      if (ctl.current === c) {
        ctl.current = null;
        setLine("");
      }
    }
  }, []);

  const stop = useCallback(() => {
    ctl.current?.abort();
    ctl.current = null;
    setLine("");
  }, []);

  return { fit, line, fitting: line !== "", stop };
}

/** What to tell the colleague when fit failed; "" when they stopped it. */
export function whatFailed(e: unknown): string {
  if (e === null) return "";
  return e instanceof VideoFitError ? e.message : "影片压不了：再选一次试试";
}
